import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

// Components
import PropertyCard from "@/components/property/PropertyCard";
import { PropertyPreview } from "@/components/property/PropertyPreview";

// Hooks & Types
import { useProperties } from "@/hooks/useProperties";
import type { Property } from "@/hooks/useProperties";

import { PropertyCardSkeleton } from "@/components/property/PropertyCardSkeleton";

const FeaturedProperties: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { properties, loading } = useProperties(i18n.language);

  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // ✅ Dernières 3 propriétés (plus logique que slice(0,3) si API renvoie tri asc)
  const featuredProperties = useMemo(() => {
    if (!properties || properties.length === 0) return [];
    return [...properties].slice(0, 3);
    // Si tu veux vraiment les "dernières" :
    // return [...properties].slice(-3).reverse();
  }, [properties]);

  const handlePreview = (property: Property) => {
    setSelectedProperty(property);
    setIsPreviewOpen(true);
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    setSelectedProperty(null);
  };

  return (
    <section className="relative py-36 sm:py-44 px-4 sm:px-6 overflow-hidden bg-cream">
      <div className="max-w-7xl mx-auto">
        {/* HEADER SECTION */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-24"
        >
          <span className="block font-oswald font-light uppercase tracking-[0.45em] text-stone-400 text-[10px] mb-6">
            {t("featured.eyebrow", "Sélection Exclusive")}
          </span>

          <h2 className="font-oswald font-light uppercase tracking-wide text-4xl sm:text-5xl md:text-6xl text-stone-800 leading-[1.1]">
            {t("featured.title", "Dernières Opportunités")}
          </h2>

          <p className="mt-10 font-playfair italic text-base sm:text-lg text-stone-500/70">
            {t(
              "featured.subtitle",
              "Une collection de propriétés d'exception sélectionnées pour leur caractère unique."
            )}
          </p>
        </motion.div>

        {/* GRID SECTION */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[1, 2, 3].map((i) => (
              <PropertyCardSkeleton key={i} />
            ))}
          </div>
        ) : featuredProperties.length > 0 ? (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-12"
          >
            {featuredProperties.map((property, index) => (
              <motion.div
                key={property.cod_ofer}
                initial={{ opacity: 0, y: 48 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.9,
                  delay: index * 0.18,
                  ease: [0.22, 1, 0.36, 1],
                }}
                viewport={{ once: true }}
              >
                <PropertyCard property={property} onPreview={handlePreview} />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-20">
            <p className="font-playfair italic text-stone-400 text-lg">
              {t("featured.empty", "Aucune propriété disponible pour le moment.")}
            </p>
          </div>
        )}

        {/* BOTTOM CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-24 text-center"
        >
          <Link
            to="/properties"
            className="
              inline-block px-14 py-5
              border border-stone-200
              text-stone-500 font-oswald text-[10px] tracking-[0.3em] uppercase
              hover:bg-stone-50 hover:text-stone-800 hover:border-stone-300
              transition-all duration-500
            "
          >
            {t("featured.cta", "Voir tout le catalogue")}
          </Link>
        </motion.div>
      </div>

      {/* MODAL PREVIEW */}
      <AnimatePresence>
        {isPreviewOpen && selectedProperty && (
          <PropertyPreview
            property={selectedProperty}
            isOpen={isPreviewOpen}
            onClose={closePreview}
            lang={i18n.language}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default FeaturedProperties;
